import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduleKeys } from './queryKeys';
import {
  fetchMonthlyRoster,
  saveScheduledShifts,
  publishMonthlyRoster,
  deleteScheduledShifts,
  deleteIndividualShifts,
  fetchShiftCodes,
  seedDefaultShiftCodes,
  type ScheduledShiftInput,
  type IndividualShiftDeletion,
} from '@/services/schedule/rosterService';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook to fetch monthly roster data
 */
export const useMonthlyRoster = (
  companyId: string | undefined,
  year: number,
  month: number
) => {
  return useQuery({
    queryKey: scheduleKeys.monthlyRoster(companyId || '', year, month),
    queryFn: () => fetchMonthlyRoster(year, month),
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch shift codes
 */
export const useShiftCodes = (companyId: string | undefined) => {
  return useQuery({
    queryKey: scheduleKeys.shiftCodes(companyId || ''),
    queryFn: fetchShiftCodes,
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000, // 10 minutes (shift codes don't change often)
  });
};

/**
 * Hook to save scheduled shifts (bulk)
 */
export const useSaveScheduledShifts = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (shifts: ScheduledShiftInput[]) => saveScheduledShifts(shifts),
    onSuccess: (data) => {
      // Invalidate roster queries to refetch
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      
      toast({
        title: 'Roster saved',
        description: `Successfully saved ${data.created_count} shift entries`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error saving roster',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to publish roster to weekly schedule
 */
export const usePublishRoster = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      publishMonthlyRoster(year, month),
    onSuccess: (data) => {
      // Invalidate both roster and shifts queries
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      
      const skippedMsg = data.skipped_count ? ` (${data.skipped_count} skipped)` : '';
      toast({
        title: 'Roster published',
        description: `Successfully published ${data.published_count || 0} shifts to the weekly schedule${skippedMsg}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error publishing roster',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to delete scheduled shifts
 */
export const useDeleteScheduledShifts = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      year,
      month,
      profileIds,
    }: {
      year: number;
      month: number;
      profileIds?: string[];
    }) => deleteScheduledShifts(year, month, profileIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      
      toast({
        title: 'Shifts deleted',
        description: `Successfully deleted ${data.deleted_count} shift entries`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error deleting shifts',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to delete individual scheduled shifts
 */
export const useDeleteIndividualShifts = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (deletions: IndividualShiftDeletion[]) => deleteIndividualShifts(deletions),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      
      toast({
        title: 'Shifts cleared',
        description: `Successfully cleared ${data.deleted_count} shift${data.deleted_count !== 1 ? 's' : ''}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error clearing shifts',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to seed default shift codes
 */
export const useSeedDefaultShiftCodes = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: () => seedDefaultShiftCodes(),
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      
      toast({
        title: 'Shift codes created',
        description: `Successfully created ${count} default shift codes`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error creating shift codes',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
